import React, { useState, useEffect } from 'react';
import { Typography, Tag } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Card from '../card/Card';
import Modal from '../modal/Modal';
import SynchronizationService from '../../services/synchronizationService';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate, formatDuration } from '../../helpers/datas';
import { DateTime } from 'luxon';
import Loading from '../Loading';

const { Text } = Typography;

const SynchronizationHistory = ({ synchronizationId, asModal = false, open = false, onClose, title = 'Histórico de Logs', data }) => {
  const { theme } = useTheme();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadHistory = async () => {
      if (Array.isArray(data) && data.length >= 0) {
        setHistory(data);
        return;
      }
      if (!synchronizationId) return;
      try {
        setLoading(true);
        const response = await SynchronizationService.getSynchronizationByIdHistory(synchronizationId);
        setHistory(response?.response || []);
      } catch (error) {
        console.error('Erro ao buscar histórico:', error);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [synchronizationId, data]);

  // Layout simplificado: removidas funções de estilo/texto não utilizadas

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Sucesso':
        return <CheckCircleOutlined style={{ color: theme.success, fontSize: 14 }} />;
      case 'falha':
        return <ExclamationCircleOutlined style={{ color: theme.error, fontSize: 14 }} />;
      case 'warning':
        return <ClockCircleOutlined style={{ color: theme.warning, fontSize: 14 }} />;
      default:
        return <ClockCircleOutlined style={{ color: theme.textSecondary, fontSize: 14 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return theme.success;
      case 'error':
        return theme.error;
      case 'warning':
        return theme.warning;
      default:
        return theme.textSecondary;
    }
  };

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <ClockCircleOutlined style={{ fontSize: 16, color: theme.primary }} />
      <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
    </div>
  );

  const formatDateWithSeconds = (isoDate) => {
    if (!isoDate) return '-';
    try {
      return DateTime.fromISO(isoDate, { zone: 'utc' })
        .setZone('America/Sao_Paulo')
        .toFormat('dd/MM/yyyy HH:mm:ss');
    } catch {
      return '-';
    }
  };

  // Helpers para normalizar datas e calcular duração em segundos
  const toIsoString = (value) => {
    if (!value && value !== 0) return null;
    if (typeof value === 'number') {
      // assume epoch em segundos
      return new Date(value * 1000).toISOString();
    }
    if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
      const secs = parseFloat(value);
      return new Date(secs * 1000).toISOString();
    }
    return value; // ISO ou outro formato de data válido
  };

  const toEpochSeconds = (value) => {
    if (!value && value !== 0) return null;
    if (typeof value === 'number') return value; // já em segundos
    if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) return parseFloat(value);
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) return null;
    return Math.floor(ms / 1000);
  };

  const getDurationSeconds = (startValue, endValue) => {
    const startSec = toEpochSeconds(startValue);
    const endSec = toEpochSeconds(endValue);
    if (startSec == null && endSec == null) return 0;
    if (startSec != null && endSec == null) {
      const nowSec = Math.floor(Date.now() / 1000);
      return Math.max(0, nowSec - startSec);
    }
    if (startSec == null && endSec != null) return 0;
    return Math.max(0, endSec - startSec);
  };

  const content = (
    <div>
      {loading && (
        <Loading small />
      )}

      {!loading && history.map((item) => {
        const startIso = toIsoString(item.startTime);
        const endIso = toIsoString(item.endTime);
        const start = startIso ? formatDateWithSeconds(startIso) : '-';
        const end = endIso ? formatDateWithSeconds(endIso) : '-';
        const durationSecs = getDurationSeconds(item.startTime, item.endTime);
        const duration = formatDuration(durationSecs);
        const statusRaw = (item.status || '').toString().toLowerCase();
        const isRunning = !endIso && !!startIso;
        const statusNorm = isRunning ? 'warning' : (statusRaw === 'sucesso' ? 'success' : 'error');

        return (
          <div key={item.id} style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${theme.border}`,
            background: theme.white,
            marginBottom: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              {getStatusIcon(statusNorm)}
              <Tag color={getStatusColor(statusNorm)} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
                {statusNorm === 'success' ? 'Sucesso' : statusNorm === 'warning' ? 'Em andamento' : 'Falha'}
              </Tag>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 8,
              marginBottom: 10
            }}>
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px' }}>
                <Text style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Início</Text>
                <Text style={{ fontSize: 13, color: theme.textSecondary }}>{start}</Text>
              </div>
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px' }}>
                <Text style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Fim</Text>
                <Text style={{ fontSize: 13, color: theme.textSecondary }}>{end}</Text>
              </div>
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px' }}>
                <Text style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Duração</Text>
                <Text style={{ fontSize: 13, color: theme.textSecondary }}>{duration}</Text>
              </div>
            </div>
          </div>
        );
      })}

      {!loading && history.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <ClockCircleOutlined style={{ fontSize: 32, color: '#bfbfbf', marginBottom: 8 }} />
          <Text style={{ display: 'block', color: '#8c8c8c' }}>
            Nenhuma execução encontrada
          </Text>
        </div>
      )}
    </div>
  );

  if (asModal) {
    return (
      <Modal
        open={open}
        centered
        onCancel={onClose}
        footer={null}
        width={560}
        title={header}
      >
        {content}
      </Modal>
    );
  }

  return (
    <Card 
      title={header}
      style={{ marginBottom: 24, borderRadius: 12 }}
      bodyStyle={{ padding: '16px' }}
    >
      {content}
    </Card>
  );
};

export default SynchronizationHistory; 